import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing or invalid reset link.");
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords don’t match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-slate-50">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-10 text-slate-100 lg:flex lg:w-[46%]">
        <div className="absolute -top-24 -left-20 h-80 w-80 rounded-full bg-sky-500/30 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 translate-x-1/3 translate-y-1/3 rounded-full bg-blue-700/30 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/dolphin-logo.png" alt="Dolphin Digital OOH Media" className="h-16 w-auto object-contain" />
        </div>
        <div className="relative space-y-5">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">Choose a new<br /><span className="bg-gradient-to-r from-sky-300 to-blue-200 bg-clip-text text-transparent">password.</span></h1>
          <p className="max-w-md text-sm leading-relaxed text-slate-300">Pick something strong—at least 8 characters, with a mix of letters, numbers, and symbols.</p>
        </div>
        <p className="relative text-xs text-slate-500">Dolphin Digital OOH Media © {new Date().getFullYear()}</p>
      </div>

      <div className="relative flex flex-1 items-center justify-center bg-grid p-6 lg:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden rounded-xl bg-slate-950 px-4 py-3">
            <img src="/dolphin-logo.png" alt="Dolphin Digital OOH Media" className="h-12 w-auto object-contain" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5">
            {done ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Password updated</h2>
                <p className="mt-2 text-sm text-slate-600">You can now sign in with your new password. Redirecting…</p>
                <Link to="/login" className="mt-4 inline-block text-sm font-medium text-sky-700 hover:text-sky-900">Go to sign in →</Link>
              </div>
            ) : !token ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Invalid link</h2>
                <p className="mt-2 text-sm text-slate-600">This password reset link is missing or invalid. Please request a new one.</p>
                <Link to="/forgot-password" className="mt-4 inline-block text-sm font-medium text-sky-700 hover:text-sky-900">Request new link →</Link>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Set new password</h2>
                <p className="mt-1 text-sm text-slate-500">Choose a strong password you haven’t used before.</p>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {error && <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
                  <div className="space-y-2">
                    <Label htmlFor="pw">New password</Label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="pl-9" required minLength={8} />
                    </div>
                    <p className="text-xs text-slate-500">At least 8 characters.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pw2">Confirm new password</Label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="pl-9" required minLength={8} />
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 transition-colors" disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? "Updating…" : "Update password"}
                  </Button>
                  <p className="text-center text-xs text-slate-500"><Link to="/login" className="font-medium text-sky-700 hover:text-sky-900">← Back to sign in</Link></p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
